import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import Section from "../components/Section";
import { useLocation } from "react-router-dom";
import { useEffect } from "react";

export default function Home() {
  const { t } = useTranslation();

  const servicesRaw = t("company.services", { returnObjects: true });
  const blurbsRaw = t("home.services_blurbs", { returnObjects: true });
  const iconsRaw = t("company.services_icons", { returnObjects: true });

  const services = Array.isArray(servicesRaw) ? servicesRaw : [];
  const serviceBlurbs = Array.isArray(blurbsRaw) ? blurbsRaw : [];
  const serviceIcons = Array.isArray(iconsRaw) ? iconsRaw : [];

  /**
   * ---- CONTACT FORM (DISABLED & PRESERVED) ----
   * The entire contact feature (state, effects, handlers) is commented out.
   * To re-enable later, remove this block comment and the JSX comment near the bottom.
   *
   * import { useRef, useEffect, useState } from "react";
   *
   * const [submitting, setSubmitting] = useState(false);
   * const [status, setStatus] = useState({ type: "", msg: "" });
   *
   * const tsRef = useRef(null);
   * useEffect(() => {
   *   if (tsRef.current == null) tsRef.current = Date.now();
   * }, []);
   *
   * useEffect(() => {
   *   if (!status.msg) return;
   *   const id = setTimeout(() => setStatus({ type: "", msg: "" }), 2000);
   *   return () => clearTimeout(id);
   * }, [status.msg]);
   *
   * const [form, setForm] = useState({
   *   name: "",
   *   email: "",
   *   phone: "",
   *   country: "",
   *   cv: null,
   * });
   *
   * const validate = () => {
   *   if (!form.name.trim()) return t("contact.errors.name_required");
   *   if (!form.email.trim()) return t("contact.errors.email_required");
   *   if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
   *     return t("contact.errors.email_invalid");
   *   if (!form.phone.trim()) return t("contact.errors.phone_required");
   *   if (!form.country.trim()) return t("contact.errors.country_required");
   *   if (!form.cv) return t("contact.errors.cv_required");
   *   const max = 10 * 1024 * 1024;
   *   if (form.cv && form.cv.size > max) return t("contact.errors.cv_too_big");
   *   return "";
   * };
   *
   * const onFile = (e) => {
   *   const file = e.target.files?.[0] || null;
   *   setForm((f) => ({ ...f, cv: file }));
   * };
   *
   * const onSubmit = async (e) => {
   *   e.preventDefault();
   *   setStatus({ type: "", msg: "" });
   *
   *   const err = validate();
   *   if (err) {
   *     setStatus({ type: "error", msg: err });
   *     return;
   *   }
   *
   *   const data = new FormData();
   *   data.append("name", form.name);
   *   data.append("email", form.email);
   *   data.append("phone", form.phone);
   *   data.append("country", form.country);
   *
   *   const uiLang = (i18n.language || navigator.language || "en")
   *     .slice(0, 2)
   *     .toLowerCase();
   *   data.append("locale", uiLang);
   *
   *   if (form.cv) data.append("cv", form.cv);
   *   data.append("_ts", String(tsRef.current));
   *   data.append("website", "");
   *
   *   setSubmitting(true);
   *   try {
   *     const res = await fetch("/api/contact", { method: "POST", body: data });
   *     const json = await res.json().catch(() => ({}));
   *     if (!res.ok) throw new Error(json?.error || t("contact.errors.generic"));
   *     setStatus({ type: "success", msg: t("contact.status.success") });
   *     setForm({ name: "", email: "", phone: "", country: "", cv: null });
   *     const input = document.getElementById("cv-input");
   *     if (input) input.value = "";
   *     tsRef.current = Date.now();
   *   } catch (err) {
   *     setStatus({ type: "error", msg: err.message });
   *   } finally {
   *     setSubmitting(false);
   *   }
   * };
   * ---- END CONTACT FORM (DISABLED) ----
   */

  const location = useLocation();

  // put this icon somewhere near the component (or in your icons file)
  function MailIcon({ className = "h-4 w-4" }) {
    return (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
        aria-hidden="true"
      >
        <path d="M4 6h16a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2z" />
        <path d="m22 8-10 6L2 8" />
      </svg>
    );
  }

  useEffect(() => {
    if (location.hash === "#contact-info") {
      const el = document.getElementById("contact-info");
      if (el) {
        setTimeout(
          () => el.scrollIntoView({ behavior: "smooth", block: "start" }),
          0
        );
      }
    }
  }, [location]);

  const scrollToContact = (e) => {
    e.preventDefault();
    const el = document.getElementById("contact-info");
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      if (typeof window !== "undefined" && window.history?.replaceState) {
        window.history.replaceState(null, "", "#contact-info");
      }
    }
  };

  return (
    <>
      <section className="relative md:min-h-[70vh] lg:min-h-[85vh] overflow-hidden bg-black isolate">
        <div className="absolute inset-0 -z-20">
          <img
            src="/hero/background.png"
            alt=""
            className="w-full h-full object-cover opacity-40"
            loading="eager"
            fetchPriority="high"
            draggable="false"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60" />
        </div>

        <div className="max-w-6xl mx-auto px-4 py-16 md:py-28 lg:py-32 h-full flex items-center">
          <div className="w-full">
            <div className="mb-8 md:mb-12">
              <p className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight text-center leading-tight md:whitespace-nowrap">
                {t("home.hero_subtitle")}
              </p>
            </div>

            <div className="grid md:grid-cols-[1fr_auto_1fr] items-center w-full gap-6 md:gap-10 lg:gap-12">
              <div className="flex flex-col items-center md:items-end text-center md:text-right gap-4 w-full">
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold leading-tight">
                  {t("home.hero_who_we_are")}
                </h2>
                <div className="flex flex-col gap-3 w-full max-w-[14rem] md:max-w-[16rem]">
                  <Link
                    to="/about"
                    className="w-full px-4 py-2.5 rounded-xl border border-neutral-700 font-bold hover:bg-white hover:text-black transition"
                    aria-label={t("nav.about")}
                  >
                    {t("nav.about")}
                  </Link>
                  <Link
                    to="/team"
                    className="w-full px-4 py-2.5 rounded-xl border border-neutral-700 font-bold hover:bg-white hover:text-black transition"
                    aria-label={t("nav.team")}
                  >
                    {t("nav.team")}
                  </Link>
                </div>
              </div>

              <div className="flex justify-center">
                <div className="h-px w-16 bg-white/20 md:h-48 md:w-px" />
              </div>

              <div className="flex flex-col items-center md:items-start text-center md:text-left gap-4 w-full">
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold leading-tight">
                  {t("home.hero_services_for_you")}
                </h2>
                <div className="flex flex-col gap-3 w-full max-w-[14rem] md:max-w-[16rem]">
                  <Link
                    to="/services"
                    className="w-full px-4 py-2.5 rounded-xl border border-neutral-700 font-bold hover:bg-white hover:text-black transition"
                    aria-label={t("nav.services")}
                  >
                    {t("nav.services")}
                  </Link>
                  <a
                    href="#contact-info"
                    onClick={scrollToContact}
                    className="w-full px-4 py-2.5 rounded-xl bg-white text-black font-bold hover:opacity-90 transition"
                    aria-label={t("home.cta_contact")}
                  >
                    {t("home.cta_contact")}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="absolute bottom-0 left-0 right-0 h-px bg-black" />
      </section>
      <Section
        id="overview"
        title={t("company.overview_heading")}
        withDivider={false}
      >
        <div className="rounded-2xl border border-neutral-800 p-6">
          {(() => {
            const raw = t("company.what_sets_us_apart");
            const parts = String(raw)
              .split(/\s*\n\s*/g)
              .filter(Boolean);
            const [first, ...rest] = parts;

            return (
              <div className="relative pl-4 md:pl-5">
                <span
                  className="absolute left-0 top-1 bottom-1 w-1 rounded-full bg-white/10"
                  aria-hidden="true"
                />

                {first && (
                  <p className="text-base md:text-lg font-semibold leading-relaxed">
                    {first}
                  </p>
                )}

                {rest.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {rest.map((line, i) => (
                      <p key={i} className="text-muted">
                        {line}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      </Section>

      <Section id="services" withDivider>
        <div className="mb-6 flex items-center justify-between gap-4">
          <h2 className="text-2xl md:text-3xl font-extrabold">
            {t("home.services_title")}
          </h2>

          <Link
            to="/services"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-neutral-700 font-semibold hover:bg-white hover:text-black transition"
            aria-label={t("nav.services")}
          >
            {t("nav.services")}
            <svg
              className="h-4 w-4"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M5 12h14M13 5l7 7-7 7"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </Link>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          {services.map((s, i) => {
            const iconSrc =
              (serviceIcons && serviceIcons[i]) ||
              "/services/career-counselling.svg";
            const blurb =
              (serviceBlurbs && serviceBlurbs[i]) ||
              t("home.service_default_blurb");

            return (
              <div
                key={i}
                className="rounded-2xl border border-neutral-800 bg-white/5 backdrop-blur-sm p-5 hover:border-neutral-600 hover:-translate-y-0.5 transition"
              >
                <div className="flex items-start gap-3">
                  <div className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-neutral-700">
                    <img
                      src={iconSrc}
                      alt=""
                      className="h-5 w-5"
                      loading="lazy"
                      width={20}
                      height={20}
                    />
                  </div>
                  <div className="text-lg font-semibold">{s}</div>
                </div>
                <p className="mt-2 text-sm text-neutral-300">{blurb}</p>
              </div>
            );
          })}
        </div>
      </Section>

      <Section id="approach" title={t("about.approach_title")} withDivider>
        {(() => {
          const titles =
            t("about.approach_points", { returnObjects: true }) || [];
          const descs = t("about.approach_desc", { returnObjects: true }) || [];

          const Icon = ({ i }) => {
            const base = "h-6 w-6 stroke-current";
            if (i === 0) {
              return (
                <svg viewBox="0 0 24 24" fill="none" className={base}>
                  <path
                    d="M13 2L3 14h7l-1 8 11-14h-7l0-6z"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              );
            }
            if (i === 1) {
              return (
                <svg viewBox="0 0 24 24" fill="none" className={base}>
                  <path
                    d="M4 19h16M4 5h16M8 9h8M8 15h5"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                  <path
                    d="M18 14l2 2-5 5h-2v-2l5-5z"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              );
            }
            return (
              <svg viewBox="0 0 24 24" fill="none" className={base}>
                <path
                  d="M12 2a10 10 0 100 20 10 10 0 000-20z"
                  strokeWidth="2"
                />
                <path
                  d="M2 12h20M12 2c3 3.5 3 16.5 0 20M5 5c4 2 10 2 14 0"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            );
          };

          return (
            <div className="grid md:grid-cols-3 gap-4">
              {titles.map((title, i) => (
                <div
                  key={i}
                  className="group rounded-2xl border border-neutral-800 bg-white/5 backdrop-blur-sm p-6 hover:border-neutral-600 transition"
                >
                  <div className="mb-4 inline-flex items-center justify-center rounded-xl border border-neutral-700 p-3 text-white">
                    <Icon i={i} />
                  </div>
                  <h3 className="text-lg font-bold mb-2">{title}</h3>
                  <p className="text-sm text-neutral-300">
                    {descs[i] ||
                      (i === 0
                        ? "We present first shortlisted candidates in 2–3 days, without lowering the bar for quality or culture fit."
                        : i === 1
                        ? "Clear, data-backed guidance on funnels, conversion rates, and salary bands so you can decide with confidence."
                        : "We understand stacks, roles, and talent pools across the tech market—so outreach is targeted and relevant.")}
                  </p>
                </div>
              ))}
            </div>
          );
        })()}
      </Section>

      <Section id="facts" title={t("about.facts_title")} withDivider>
        <div className="mb-4">
          <p className="text-neutral-300">
            We turn challenging hiring needs into success stories. From
            hard-to-fill senior developer roles open for nearly a year, to
            scaling entire tech teams in record time, and even “impossible”
            low-budget positions — we deliver results fast and efficiently. Here
            are our most recent wins:
          </p>
        </div>

        {(() => {
          const Check = () => (
            <svg
              viewBox="0 0 24 24"
              className="h-5 w-5 text-green-400"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M20 7L9 18l-5-5"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          );

          const stories = [
            {
              title: "2 Senior Angular Developer roles — open for 9+ months",
              result: "Closed by us in 3 weeks.",
            },
            {
              title:
                "Two teams of 10 developers for a fast-scaling startup needing urgent hires",
              result: "Closed by us in 1 month.",
            },
            {
              title:
                "3 Dutch-speaking Customer Support roles — night shifts, very low budget, considered “impossible”",
              result: "Closed by us in 3 weeks.",
            },
            {
              title:
                "Country Manager — helped a Romanian company open their first office in Budapest, Hungary",
              result: "Closed by us in 1 month.",
            },
          ];

          return (
            <div className="grid sm:grid-cols-2 gap-4">
              {stories.map((s, i) => (
                <div
                  key={i}
                  className="group rounded-2xl border border-neutral-800 bg-white/5 backdrop-blur-sm p-5 hover:border-neutral-600 transition"
                >
                  <div className="flex items-start gap-3">
                    <div className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-neutral-700">
                      <Check />
                    </div>
                    <div className="space-y-2">
                      <h3 className="text-base md:text-lg font-semibold text-white">
                        {s.title}
                      </h3>
                      <div className="inline-flex items-center gap-2">
                        <span className="rounded-full bg-green-500/10 text-green-400 border border-green-500/30 px-2.5 py-1 text-xs font-semibold">
                          Result
                        </span>
                        <p className="text-sm text-neutral-300">{s.result}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          );
        })()}
      </Section>

      <Section id="expertise" title="Area of Expertise" withDivider>
        <p className="text-neutral-300 mb-6">
          We connect top talent across every critical function of your business.
          Our recruitment expertise includes, but is not limited to:
        </p>

        {(() => {
          const Icon = ({ name }) => {
            const base = "h-5 w-5";
            switch (name) {
              case "dev":
                return (
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    className={base}
                    aria-hidden="true"
                  >
                    <path
                      d="M8 16l-4-4 4-4M16 8l4 4-4 4M14 4l-4 16"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                );
              case "ai":
                return (
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    className={base}
                    aria-hidden="true"
                  >
                    <path
                      d="M12 3v3M12 18v3M4.22 4.22l2.12 2.12M17.66 17.66l2.12 2.12M3 12h3M18 12h3M4.22 19.78l2.12-2.12M17.66 6.34l2.12-2.12"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                    <circle
                      cx="12"
                      cy="12"
                      r="4"
                      stroke="currentColor"
                      strokeWidth="2"
                    />
                  </svg>
                );
              case "clevel":
                return (
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    className={base}
                    aria-hidden="true"
                  >
                    <path
                      d="M12 7a4 4 0 1 0 0-8 4 4 0 0 0 0 8z"
                      transform="translate(0 6)"
                      stroke="currentColor"
                      strokeWidth="2"
                    />
                    <path
                      d="M4 22v-2a4 4 0 0 1 4-4h8a4 4 0 0 1 4 4v2"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                    <path
                      d="M8 6l4-2 4 2-4 2-4-2z"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                );
              case "marketing":
                return (
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    className={base}
                    aria-hidden="true"
                  >
                    <path
                      d="M3 10l12-7v18L3 14V10z"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M21 12a3 3 0 0 1-3 3"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                );
              case "hr":
                return (
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    className={base}
                    aria-hidden="true"
                  >
                    <path
                      d="M16 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                    <circle
                      cx="9"
                      cy="8"
                      r="4"
                      stroke="currentColor"
                      strokeWidth="2"
                    />
                    <path
                      d="M20 21v-2a4 4 0 0 0-3-3.87M17 3a4 4 0 1 1 0 8"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                );
              case "legal":
                return (
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    className={base}
                    aria-hidden="true"
                  >
                    <path
                      d="M12 3l8 4-8 4-8-4 8-4z"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M4 11l8 4 8-4M4 17l8 4 8-4"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinejoin="round"
                    />
                  </svg>
                );
              case "qa":
                return (
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    className={base}
                    aria-hidden="true"
                  >
                    <path
                      d="M9 11l2 2 4-4"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <rect
                      x="3"
                      y="3"
                      width="18"
                      height="14"
                      rx="2"
                      ry="2"
                      stroke="currentColor"
                      strokeWidth="2"
                    />
                    <path
                      d="M7 21h10"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                );
              default:
                return null;
            }
          };

          const blocks = [
            {
              icon: "dev",
              title: "Development & IT",
              roles: "Developers, DevOps, IT specialists",
            },
            {
              icon: "ai",
              title: "AI / ML",
              roles: "Data Scientists, Data Engineers, Data Architects",
            },
            {
              icon: "clevel",
              title: "Management & C-Level",
              roles:
                "Executives, Team Leads, Directors, Architects, Country Managers",
            },
            {
              icon: "marketing",
              title: "Marketing & Sales",
              roles: "Marketing, PPC, Affiliate, Sales Teams",
            },
            {
              icon: "hr",
              title: "HR & Customer Support",
              roles: "Recruiter, HR Manager, Customer Service",
            },
            {
              icon: "legal",
              title: "Legal & Accounting",
              roles: "Legal Advisor, Finance Director, Accountant",
            },
            {
              icon: "qa",
              title: "QA",
              roles: "Manual & Automation Testing",
            },
          ];

          return (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {blocks.map((b, i) => (
                <div
                  key={i}
                  className="group rounded-2xl border border-neutral-800 bg-white/5 backdrop-blur-sm p-5 hover:border-neutral-600 hover:-translate-y-0.5 transition"
                >
                  <div className="flex items-start gap-3">
                    <div className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-neutral-700 text-white">
                      <Icon name={b.icon} />
                    </div>
                    <div className="space-y-1">
                      <h3 className="text-base md:text-lg font-semibold text-white">
                        {b.title}
                      </h3>
                      <p className="text-sm text-neutral-300">{b.roles}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          );
        })()}
      </Section>

      <Section id="contact-info" withDivider>
        <div className="mb-4 flex items-center justify-between gap-4">
          <h2 className="text-2xl md:text-3xl font-extrabold">
            {t("contact.title")}
          </h2>

          <Link
            to="/about"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-neutral-700 font-semibold hover:bg-white hover:text-black transition"
            aria-label={t("nav.about")}
          >
            {t("nav.about")}
            <svg
              className="h-4 w-4"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M5 12h14M13 5l7 7-7 7"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </Link>
        </div>

        <p className="text-neutral-400">{t("contact.intro")}</p>

        <div className="grid md:grid-cols-3 gap-4 mt-6">
          <div className="rounded-2xl border border-neutral-800 p-4">
            <strong className="block">{t("contact.hq")}</strong>
            <p className="text-neutral-400 text-sm leading-relaxed">
              Bucharest, Romania
            </p>
          </div>

          <div className="rounded-2xl border border-neutral-800 p-4">
            <strong className="block">{t("contact.email_us")}</strong>
            <ul className="mt-1 space-y-2 text-neutral-300">
              <li className="flex items-center gap-2">
                <MailIcon className="h-4 w-4 text-neutral-400" />
                <a
                  className="underline hover:no-underline"
                  href="mailto:theodorz@2getters.com"
                >
                  theodorz@2getters.com
                </a>
              </li>
              <li className="flex items-center gap-2">
                <MailIcon className="h-4 w-4 text-neutral-400" />
                <a
                  className="underline hover:no-underline"
                  href="mailto:adinap@2getters.com"
                >
                  adinap@2getters.com
                </a>
              </li>
            </ul>
          </div>

          <div className="rounded-2xl border border-neutral-800 p-4">
            <a
              href="https://www.linkedin.com/company/2getters"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full px-4 py-2 bg-white text-black hover:bg-neutral-100 border border-black transition focus:outline-none focus:ring-2 focus:ring-black/30"
              aria-label="Open LinkedIn company page"
              title="LinkedIn"
            >
              {/* LinkedIn icon (white) */}
              <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true">
                <path
                  fill="currentColor"
                  d="M20.447 20.452h-3.555v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.447-2.136 2.943v5.663H9.35V9h3.414v1.561h.049c.476-.9 1.637-1.852 3.369-1.852 3.602 0 4.266 2.371 4.266 5.455v6.288zM5.337 7.433a2.064 2.064 0 1 1 0-4.128 2.064 2.064 0 0 1 0 4.128zM3.56 20.452h3.555V9H3.56v11.452z"
                />
              </svg>
              <span>LinkedIn</span>
            </a>
          </div>
        </div>
      </Section>
    </>
  );
}
